import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getChatMessages, sendChatMessage } from '../services/api';

const Chat = () => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (!userData) {
            navigate('/login');
            return;
        }
        setUser(JSON.parse(userData));
    }, [navigate]);

    const roomId = user?.rooms === '*' ? '101' : user?.rooms?.split(',')[0];

    const fetchMessages = async () => {
        if (!roomId) return;
        try {
            const response = await getChatMessages(roomId);
            if (response.data) {
                setMessages(response.data);
            }
        } catch (err) {
            console.error("Error fetching messages:", err);
        }
    };

    useEffect(() => {
        if (roomId) {
            fetchMessages();
            const interval = setInterval(fetchMessages, 5000);
            return () => clearInterval(interval);
        }
    }, [roomId]);

    useEffect(() => {
        //messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            // Directly set scroll position to the bottom of the container
            container.scrollTo({
                top: container.scrollHeight,
                behavior: "smooth"
            });
        }
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !roomId) return;

        setLoading(true);
        try {
            await sendChatMessage(roomId, newMessage);
            setNewMessage('');
            fetchMessages();
        } catch (err) {
            console.error("Error sending message:", err);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            timeZone: 'America/Los_Angeles',
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <div style={{ 
            width: '100%', 
            maxWidth: '480px', 
            margin: '0 auto',
            /* CHANGE: Use fixed position to lock the app to the viewport */
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            overflow: 'hidden' // Prevents the whole body from bouncing
        }}>
            {/* Header */}
            <header style={{
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f0f0f0',
                borderBottom: '1px solid #e0e0e0',
                flexShrink: 0
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img 
                        src="/dog_mascot.png" 
                        alt="Logo"
                        style={{ 
                            height: '32px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'inline';
                        }}
                    />
                    <span style={{ fontSize: '24px', display: 'none' }}>🐕</span>
                    <h2 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: 'bold', 
                        color: '#000',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        My Pup : Chat
                    </h2>
                </div>
                <button 
                    onClick={() => navigate('/video-stream')} 
                    style={{ 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#AE3041',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '4px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ⬆️
                </button>
            </header>

            {/* Messages */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                background: '#ffffff',
                /* CHANGE: Important for mobile momentum scrolling */
                WebkitOverflowScrolling: 'touch'
            }} ref={scrollContainerRef}>
                {messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#666', marginTop: '32px' }}>
                        No messages yet.
                    </div>
                ) : (
                    messages.map((msg) => (
                        <div key={msg.MessageId} style={{
                            marginBottom: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: msg.Sender === 'User' ? 'flex-end' : 'flex-start'
                        }}>
                            <div style={{
                                display: 'inline-block',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                maxWidth: '75%',
                                background: msg.Sender === 'User' ? '#4A90E2' : '#FFA500',
                                color: '#ffffff',
                                fontFamily: 'Arial, sans-serif'
                            }}>
                                <p style={{ margin: 0, fontSize: '14px', fontFamily: 'Arial, sans-serif' }}>{msg.Message}</p>
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                marginTop: '4px',
                                textAlign: msg.Sender === 'User' ? 'right' : 'left',
                                fontFamily: 'Arial, sans-serif'
                            }}>
                                {formatDateTime(msg.SentAt)}
                            </div>
                        </div>
                    ))
                )}
                {/*<div ref={messagesEndRef} />*/}
            </div>

            {/* Input */}
            <form onSubmit={handleSend} style={{
                padding: '12px 16px',
                background: '#ffffff',
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                gap: '8px',
                flexShrink: 0, // Ensures the form never gets squashed
                /* CHANGE: Adds padding for the iPhone "home bar" if needed */
                paddingBottom: 'env(safe-area-inset-bottom, 16px)'
            }}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Write your message here"
                    style={{
                        flex: 1,
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        background: '#f5f5f5',
                        fontSize: '14px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                />
                <button 
                    type="submit" 
                    disabled={loading || !newMessage.trim()}
                    style={{
                        padding: '12px 16px',
                        background: '#AE3041',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loading || !newMessage.trim() ? 'not-allowed' : 'pointer',
                        fontSize: '20px',
                        fontFamily: 'Arial, sans-serif'
                    }}
                >
                    ✈️
                </button>
            </form>
        </div>
    );
};

export default Chat;

